import { useEffect, useState } from 'react';
import * as yup from 'yup';
import Box from '@mui/material/Box';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from 'assets/icons/TableDelete.png';
import { useDispatch, useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import { confirm } from 'components/MenuNew/ConfirmModal';
import { MODE } from 'pages/TableOrdering/config';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import { usePosMenuOptions } from 'pages/PosSettings/hooks/usePosMenuOptions';
import {
  PROMOTION_TYPES,
  actionSchemaFields,
  actionDefaultValues,
  actionToFormValues,
  buildPromotionAction,
} from 'pages/PosSettings/Promotions/promotionAction';
import {
  getPromotionsPOS,
  createPromotionPOS,
  updatePromotionPOS,
  deletePromotionPOS,
} from 'redux/actions/Pos/promotions';
import { getCouponsPOS } from 'redux/actions/Pos/coupons';
import {
  couponSchemaFields,
  couponDefaultValues,
  couponToFormValues,
  buildCouponPayload,
} from 'pages/PosSettings/Coupons/couponForm';
import {
  optionalCount,
  requiredAmount,
  requiredCount,
} from 'pages/PosSettings/numberRules';
import {
  buildChannels,
  channelDefaultValue,
  channelsToFormValue,
  describeChannels,
} from 'pages/PosSettings/channels';

export const PROMOTION_STATUSES = [
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Inactive', value: 'INACTIVE' },
  { label: 'Draft', value: 'DRAFT' },
  { label: 'Expired', value: 'EXPIRED' },
  { label: 'Archived', value: 'ARCHIVED' },
];

export const CONDITION_TYPES = [
  { label: 'Min Order Amount', value: 'MIN_ORDER_AMOUNT' },
  { label: 'Max Order Amount', value: 'MAX_ORDER_AMOUNT' },
  { label: 'Specific Items', value: 'ITEM_UUIDS' },
  { label: 'Specific Categories', value: 'CATEGORY_UUIDS' },
  { label: 'Min Item Quantity', value: 'MIN_ITEM_QUANTITY' },
  { label: 'Day Of Week', value: 'DAY_OF_WEEK' },
  { label: 'Time Range', value: 'TIME_RANGE' },
];

export const DAY_OPTIONS = [
  { label: 'Sunday', value: 'SUNDAY' },
  { label: 'Monday', value: 'MONDAY' },
  { label: 'Tuesday', value: 'TUESDAY' },
  { label: 'Wednesday', value: 'WEDNESDAY' },
  { label: 'Thursday', value: 'THURSDAY' },
  { label: 'Friday', value: 'FRIDAY' },
  { label: 'Saturday', value: 'SATURDAY' },
];

export const CUSTOMER_GROUP_OPTIONS = [
  { label: 'VIP', value: 'VIP' },
  { label: 'Loyalty', value: 'LOYALTY' },
  { label: 'Employee', value: 'EMPLOYEE' },
  { label: 'New', value: 'NEW' },
  { label: 'Wholesale', value: 'WHOLESALE' },
];

const promotionFormSchema = yup.object().shape({
  name: yup.string().required('field_is_required_'),
  type: yup.string().required('field_is_required_'),
  trigger: yup.string().nullable(),
  code: yup.string().nullable(),
  description: yup.string().nullable(),
  status: yup.string().nullable(),
  priority: optionalCount({ min: 0 }),
  stackable: yup.boolean().nullable(),
  channels: yup.array().of(yup.string()).nullable(),
  usageLimit: optionalCount({ min: 0 }),
  usageLimitPerCustomer: optionalCount({ min: 0 }),
  startAt: yup.string().nullable(),
  endAt: yup.string().nullable(),
  displayTitle: yup.string().nullable(),
  displayShortText: yup.string().nullable(),
  displayCartLabel: yup.string().nullable(),
  displayPosLabel: yup.string().nullable(),
  displayPendingMessage: yup.string().nullable(),
  conditions: yup.array().of(
    yup.object().shape({
      type: yup.string().required('field_is_required_'),
      // A condition compares against its value, so the numeric ones cannot be
      // saved holding text or nothing at all.
      value: yup
        .mixed()
        .when('type', {
          is: (type) =>
            type === 'MIN_ORDER_AMOUNT' || type === 'MAX_ORDER_AMOUNT',
          then: requiredAmount({ min: 0 }),
        })
        .when('type', {
          is: 'MIN_ITEM_QUANTITY',
          then: requiredCount({ min: 1 }),
        }),
    })
  ),
  ...actionSchemaFields,
  ...couponSchemaFields({ requiredWithTrigger: true }),
});

const CREATE_DEFAULT_TYPE = 'BUY_X_GET_Y';

const defaultValues = {
  name: '',
  type: '',
  trigger: 'AUTOMATIC',
  code: '',
  description: '',
  status: 'ACTIVE',
  priority: '',
  stackable: true,
  channels: channelDefaultValue,
  usageLimit: '',
  usageLimitPerCustomer: '',
  startAt: '',
  endAt: '',
  displayTitle: '',
  displayShortText: '',
  displayCartLabel: '',
  displayPosLabel: '',
  displayPendingMessage: '',
  conditions: [],
  ...actionDefaultValues,
  ...couponDefaultValues,
};

const getPromoId = (promo) =>
  promo?.uuid || promo?._uuid || promo?.promotionDetail?.uuid || promo?._id;

const getDetail = (promo) => {
  if (promo?.promotionDetail) return promo.promotionDetail;
  return {
    uuid: promo?.uuid || promo?._uuid,
    name: promo?.name || promo?._name,
    type: promo?.type || promo?._type,
    trigger: promo?.trigger || promo?._trigger,
    status: promo?.status || promo?._status,
    code: promo?.code || promo?._code,
    description: promo?.description || promo?._description,
    priority: promo?.priority ?? promo?._priority,
    stackable: promo?.stackable ?? promo?._stackable,
    channels: promo?.channels || promo?._channels,
    branchIds: promo?.branchIds || promo?._branchIds,
    customerGroups: promo?.customerGroups || promo?._customerGroups,
    startAt: promo?.startAt || promo?._startAt,
    endAt: promo?.endAt || promo?._endAt,
    usageLimit: promo?.usageLimit ?? promo?._usageLimit,
    usageLimitPerCustomer: promo?.usageLimitPerCustomer ?? promo?._usageLimitPerCustomer,
    display: promo?.display || promo?._display,
    couponCodes: promo?.couponCodes || promo?._couponCodes,
    conditions: promo?.conditions || promo?._conditions,
    actions: promo?.actions || promo?._actions,
    version: promo?.version ?? promo?._version,
    metadata: promo?.metadata || promo?._metadata,
  };
};

const deserializeConditionValue = (condition) => {
  const { type, value } = condition;
  switch (type) {
    case 'MIN_ORDER_AMOUNT':
    case 'MAX_ORDER_AMOUNT':
    case 'MIN_ITEM_QUANTITY':
      return Number(value) || '';
    case 'ITEM_UUIDS':
    case 'CATEGORY_UUIDS':
    case 'CUSTOMER_GROUP':
    case 'DAY_OF_WEEK':
      return Array.isArray(value) ? value : [];
    case 'TIME_RANGE':
      if (value && typeof value === 'object') return value;
      return { from: '', to: '' };
    case 'FIRST_ORDER':
      return true;
    case 'COUPON_CODE':
      return String(value ?? '');
    default:
      return value ?? '';
  }
};

const serializeCondition = (condition) => {
  const { type, value } = condition;
  const cond = { type };
  switch (type) {
    case 'MIN_ORDER_AMOUNT':
      cond.value = Number(value) || 0;
      cond.operator = 'gte';
      break;
    case 'MAX_ORDER_AMOUNT':
      cond.value = Number(value) || 0;
      cond.operator = 'lte';
      break;
    case 'MIN_ITEM_QUANTITY':
      cond.value = Number(value) || 0;
      cond.operator = 'gte';
      break;
    case 'ITEM_UUIDS':
    case 'CATEGORY_UUIDS':
    case 'CUSTOMER_GROUP':
    case 'DAY_OF_WEEK':
      cond.value = Array.isArray(value) ? value : [];
      break;
    case 'TIME_RANGE':
      cond.value =
        value && typeof value === 'object' ? value : { from: '', to: '' };
      break;
    case 'FIRST_ORDER':
      cond.value = true;
      break;
    case 'COUPON_CODE':
      cond.value = String(value ?? '');
      break;
    default:
      cond.value = value;
  }
  return cond;
};

export const usePosPromotions = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(MODE.CREATE);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [editingId, setEditingId] = useState(null);

  const { currentBusiness } = useSelector(({ business }) => business);
  const { promotions = [], loading } = useSelector(({ pos }) => pos);
  const { productOptions, categoryOptions } = usePosMenuOptions();

  const formMethods = useForm({
    defaultValues,
    mode: 'all',
    resolver: yupResolver(promotionFormSchema),
  });

  useEffect(() => {
    if (currentBusiness?._id) {
      dispatch(getPromotionsPOS(currentBusiness?.uuid));
    }
  }, [currentBusiness?._id, location]);

  const promotionColumns = [
    {
      field: 'name',
      headerName: t('Name'),
      flex: 1.5,
      sortable: false,
      filterable: false,
      editable: false,
    },
    {
      field: 'type',
      headerName: t('Type'),
      flex: 1,
      sortable: false,
      filterable: false,
      editable: false,
      renderCell: (params) => {
        const match = PROMOTION_TYPES.find((pt) => pt.value === params.value);
        return match ? match.label : params.value;
      },
    },
    {
      field: 'status',
      headerName: t('Status'),
      flex: 0.8,
      sortable: false,
      filterable: false,
      editable: false,
      renderCell: (params) => {
        const match = PROMOTION_STATUSES.find(
          (ps) => ps.value === params.value
        );
        return match ? match.label : params.value;
      },
    },
    {
      field: 'channels',
      headerName: t('Channels'),
      flex: 1,
      sortable: false,
      filterable: false,
      editable: false,
      renderCell: (params) => t(describeChannels(params.value)),
    },
    {
      field: 'firstCode',
      headerName: t('Codes'),
      flex: 0.6,
      sortable: false,
      filterable: false,
      editable: false,
      renderCell: (params) =>
        params?.row?.trigger === 'CODE' && params.value ? params.value : '—',
    },
    {
      field: 'startAt',
      headerName: t('Start'),
      flex: 0.8,
      sortable: false,
      filterable: false,
      editable: false,
      renderCell: (params) => {
        if (!params.value) return '—';
        return new Date(params.value).toLocaleDateString();
      },
    },
    {
      field: 'endAt',
      headerName: t('End'),
      flex: 0.8,
      sortable: false,
      filterable: false,
      editable: false,
      renderCell: (params) => {
        if (!params.value) return '—';
        return new Date(params.value).toLocaleDateString();
      },
    },
    {
      id: 'tools',
      field: 'Tools',
      type: 'actions',
      width: 100,
      renderCell: (params) => {
        return (
          <Box sx={{ display: 'flex' }}>
            <IconButton
              aria-label="edit"
              onClick={() => editPromotion(params?.row)}
            >
              <ModeEditOutlineOutlinedIcon />
            </IconButton>
            <IconButton
              aria-label="delete"
              onClick={() => deletePromotion(params?.row)}
            >
              <img src={DeleteIcon} alt="delete icon" />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  const promotionRows = (Array.isArray(promotions) ? promotions : []).map((promo) => {
    const d = getDetail(promo);
    return {
      _raw: promo,
      id: getPromoId(promo),
      name: d?.name || '',
      type: d?.type || '',
      status: d?.status || '',
      startAt: d?.startAt || null,
      endAt: d?.endAt || null,
      trigger: d?.trigger || 'AUTOMATIC',
      channels: d?.channels || [],
      firstCode: d?.couponCodes?.[0] || null,
    };
  });

  const deletePromotion = async (row) => {
    const confirmed = await confirm({
      title: t('Delete Promotion'),
      message: t('Are you sure you want to delete this promotion?'),
      cancelText: t('orders.cancel'),
      confirmText: t('table_ordering.yes_delete'),
      confirmColor: 'primary',
      background: 'rgba(226, 21, 8, 1)',
    });
    if (confirmed) {
      dispatch(deletePromotionPOS(row?.id, currentBusiness?.uuid));
    }
  };

  const editPromotion = async (row) => {
    const promo = row?._raw || row;
    const d = getDetail(promo);
    const firstAction = (d?.actions || [])[0] || {};
    const promotionUuid = d?.uuid || promo?.uuid;

    // The coupon is read before the dialog opens. Opening first would show the
    // form half filled and then rewrite it under the user.
    const coupon =
      d?.trigger === 'CODE' && promotionUuid
        ? (
            await dispatch(
              getCouponsPOS({
                businessUuid: currentBusiness?.uuid,
                promotionUuid,
              })
            ).catch(() => [])
          )?.[0] || null
        : null;

    formMethods.reset({
      name: d?.name || '',
      type: d?.type || '',
      trigger: d?.trigger || 'AUTOMATIC',
      code: d?.code || '',
      description: d?.description || '',
      status: d?.status || 'ACTIVE',
      priority: d?.priority ?? '',
      stackable: d?.stackable || false,
      channels: channelsToFormValue(d?.channels),
      usageLimit: d?.usageLimit ?? '',
      usageLimitPerCustomer: d?.usageLimitPerCustomer ?? '',
      startAt: d?.startAt ? d.startAt.slice(0, 10) : '',
      endAt: d?.endAt ? d.endAt.slice(0, 10) : '',
      displayTitle: d?.display?.title || '',
      displayShortText: d?.display?.shortText || '',
      displayCartLabel: d?.display?.cartLabel || '',
      displayPosLabel: d?.display?.posLabel || '',
      displayPendingMessage: d?.display?.pendingMessage || '',
      conditions: (d?.conditions || []).map((c) => ({
        type: c.type || '',
        value: deserializeConditionValue(c),
      })),
      ...actionToFormValues(firstAction),
      ...couponToFormValues(coupon),
    });
    formMethods.clearErrors();

    setEditingId(row?.id);
    setMode(MODE.EDIT);
    setOpen(true);
  };

  const buildRequestBody = (values) => {
    const body = {
      businessUuid: currentBusiness?.uuid,
      name: values.name,
      type: values.type,
      trigger: values.trigger || 'AUTOMATIC',
      channels: buildChannels(values.channels),
    };

    if (values.code) body.code = values.code;
    if (values.description) body.description = values.description;
    if (values.status) body.status = values.status;
    if (values.priority !== '' && values.priority != null)
      body.priority = Number(values.priority);
    if (values.stackable != null) body.stackable = values.stackable;
    if (values.usageLimit !== '' && values.usageLimit != null)
      body.usageLimit = Number(values.usageLimit);
    if (
      values.usageLimitPerCustomer !== '' &&
      values.usageLimitPerCustomer != null
    )
      body.usageLimitPerCustomer = Number(values.usageLimitPerCustomer);

    if (values.startAt) body.startAt = new Date(values.startAt).toISOString();
    if (values.endAt) body.endAt = new Date(values.endAt).toISOString();

    const display = {};
    if (values.displayTitle) display.title = values.displayTitle;
    if (values.displayShortText) display.shortText = values.displayShortText;
    if (values.displayCartLabel) display.cartLabel = values.displayCartLabel;
    if (values.displayPosLabel) display.posLabel = values.displayPosLabel;
    if (values.displayPendingMessage)
      display.pendingMessage = values.displayPendingMessage;
    if (Object.keys(display).length > 0) body.display = display;

    body.conditions = (values.conditions || []).map(serializeCondition);

    body.actions = [buildPromotionAction(values)];

    // A CODE promotion is unusable without its code, so the two travel together
    // and the service writes them as one. Turning the trigger off leaves any
    // existing coupon alone rather than deleting it.
    if (values.trigger === 'CODE') {
      body.coupon = buildCouponPayload({
        values,
        startAt: values.startAt
          ? new Date(values.startAt).toISOString()
          : undefined,
        endAt: values.endAt ? new Date(values.endAt).toISOString() : undefined,
        active: values.status === 'ACTIVE',
      });
    }

    return body;
  };

  /** Refetched once the write is done, instead of once per request. */
  const reload = () => dispatch(getPromotionsPOS(currentBusiness?.uuid));

  // A rejected save keeps the form open with what the user typed, so the toast
  // explains the failure and they can correct the code in place.
  const createPromotion = (values) => {
    formMethods.clearErrors();
    dispatch(createPromotionPOS(buildRequestBody(values)))
      .then(() => {
        handleCancel();
        return reload();
      })
      .catch(() => {});
  };

  const updatePromotion = (values) => {
    formMethods.clearErrors();
    dispatch(
      updatePromotionPOS({ id: editingId, body: buildRequestBody(values) })
    )
      .then(() => {
        handleCancel();
        return reload();
      })
      .catch(() => {});
  };

  const handleSubmit = (values) => {
    if (open && mode === MODE.EDIT) {
      updatePromotion(values);
    } else {
      createPromotion(values);
    }
  };

  /**
   * A new promotion opens on a concrete type, because the action fields — the
   * order/item choice and the target pickers — belong to a type and have
   * nothing to show until one is chosen.
   */
  const startCreate = () => {
    formMethods.reset({
      ...defaultValues,
      type: CREATE_DEFAULT_TYPE,
    });
    formMethods.clearErrors();
    setEditingId(null);
    setMode(MODE.CREATE);
    setOpen(true);
  };

  const handleCancel = () => {
    formMethods.reset(defaultValues);
    formMethods.clearErrors();
    setOpen(false);
    setEditingId(null);
    setTimeout(() => {
      setMode(MODE.CREATE);
    }, 300);
  };

  return {
    handleSubmit,
    handleCancel,
    formMethods,
    promotionColumns,
    promotionRows,
    loading,
    open,
    setOpen,
    startCreate,
    mode,
    rowsPerPage,
    setRowsPerPage,
    productOptions,
    categoryOptions,
  };
};
